import React, { useState } from 'react';
import { Search, MapPin, Briefcase, Clock, ChevronRight, Filter } from 'lucide-react';
import { locations } from '../data/mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const mockJobs = [
  {
    id: 1,
    title: "Directeur Financier (CFO)",
    company: "Entreprise Confidentielle",
    location: "Zurich",
    type: "CDI",
    salary: "CHF 180'000 - 220'000",
    posted: "Il y a 2 jours",
    description: "Nous recherchons un Directeur Financier expérimenté pour piloter la stratégie financière d'un groupe international."
  },
  {
    id: 2,
    title: "Responsable RH Senior",
    company: "Multinationale Pharma",
    location: "Genève",
    type: "CDI",
    salary: "CHF 140'000 - 170'000",
    posted: "Il y a 3 jours",
    description: "Poste stratégique au sein d'une entreprise pharmaceutique leader, en charge de la gestion RH pour la région EMEA."
  },
  {
    id: 3,
    title: "Legal Counsel",
    company: "Banque Privée",
    location: "Genève",
    type: "CDI",
    salary: "CHF 150'000 - 180'000",
    posted: "Il y a 5 jours",
    description: "Rejoignez le département juridique d'une banque privée prestigieuse pour conseiller sur les aspects réglementaires."
  },
  {
    id: 4,
    title: "IT Project Manager",
    company: "Scale-up Tech",
    location: "Lausanne",
    type: "CDI",
    salary: "CHF 120'000 - 150'000",
    posted: "Il y a 1 semaine",
    description: "Pilotez des projets de transformation digitale pour une scale-up en pleine croissance."
  },
  {
    id: 5,
    title: "Contrôleur de Gestion",
    company: "Groupe Industriel",
    location: "Berne",
    type: "CDI",
    salary: "CHF 100'000 - 130'000",
    posted: "Il y a 1 semaine",
    description: "Participez au pilotage financier d'un groupe industriel suisse de renom."
  },
  {
    id: 6,
    title: "Head of Compliance",
    company: "Asset Management",
    location: "Zurich",
    type: "CDI",
    salary: "CHF 200'000 - 250'000",
    posted: "Il y a 2 semaines",
    description: "Dirigez l'équipe compliance d'une société de gestion d'actifs internationale."
  }
];

const EmploisPage = () => {
  const [location, setLocation] = useState('');
  const [keyword, setKeyword] = useState('');
  const [sector, setSector] = useState('');

  const sectors = [
    { value: 'finance', label: 'Finance & Comptabilité' },
    { value: 'tech', label: 'Technology' },
    { value: 'legal', label: 'Juridique' },
    { value: 'hr', label: 'Ressources Humaines' },
    { value: 'banking', label: 'Services Financiers' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero */}
      <section className="bg-[#E30022] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-serif mb-4">Rechercher un emploi</h1>
          <p className="text-xl opacity-90">Découvrez nos opportunités de carrière en Suisse</p>
        </div>
      </section>

      {/* Search Filters */}
      <section className="bg-white shadow-md -mt-8 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid md:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="Poste, compétence, entreprise..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full h-12 px-4 border border-gray-300 rounded-md focus:border-[#E30022] focus:ring-1 focus:ring-[#E30022] outline-none"
            />
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger className="h-12 border-gray-300">
                <SelectValue placeholder="Localisation" />
              </SelectTrigger>
              <SelectContent>
                {locations.filter(l => l.value).map((loc) => (
                  <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sector} onValueChange={setSector}>
              <SelectTrigger className="h-12 border-gray-300">
                <SelectValue placeholder="Secteur" />
              </SelectTrigger>
              <SelectContent>
                {sectors.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button className="h-12 bg-[#E30022] text-white rounded-md hover:bg-[#c7001d] transition-colors flex items-center justify-center gap-2 font-medium">
              <Search className="h-5 w-5" />
              Rechercher
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <p className="text-gray-600">{mockJobs.length} offres trouvées</p>
          <button className="flex items-center gap-2 text-gray-600 hover:text-[#E30022]">
            <Filter className="h-4 w-4" />
            Filtres avancés
          </button>
        </div>

        <div className="space-y-4">
          {mockJobs.map((job) => (
            <div key={job.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow group">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-800 group-hover:text-[#E30022] transition-colors mb-2">
                    {job.title}
                  </h3>
                  <p className="text-gray-600 font-medium mb-3">{job.company}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {job.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Briefcase className="h-4 w-4" />
                      {job.type}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {job.posted}
                    </span>
                  </div>
                  <p className="text-gray-600 line-clamp-2">{job.description}</p>
                  <p className="text-[#E30022] font-semibold mt-3">{job.salary}</p>
                </div>
                <a href={`/emploi/${job.id}`} className="flex items-center gap-1 text-[#E30022] hover:underline font-medium ml-4">
                  Voir l'offre
                  <ChevronRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-12 gap-2">
          <button className="px-4 py-2 border border-gray-300 rounded hover:border-[#E30022] hover:text-[#E30022] transition-colors">Précédent</button>
          <button className="px-4 py-2 bg-[#E30022] text-white rounded">1</button>
          <button className="px-4 py-2 border border-gray-300 rounded hover:border-[#E30022] hover:text-[#E30022] transition-colors">2</button>
          <button className="px-4 py-2 border border-gray-300 rounded hover:border-[#E30022] hover:text-[#E30022] transition-colors">3</button>
          <button className="px-4 py-2 border border-gray-300 rounded hover:border-[#E30022] hover:text-[#E30022] transition-colors">Suivant</button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EmploisPage;
